/**
 * The hedgerow's sight-blockers and cover field.
 *
 * This one cover list is also the rain-shelter list: if a place conceals her
 * from eyes it shelters her from rain. Do not add a second shelter geometry.
 */

import { bakeLightGrid, type Aabb, type CoverVolume, type LightGrid } from '@/systems/stealth';
import {
  BINS,
  DITCH_AT,
  HEDGE_GAP_X,
  NIGHT_DOG_AT,
  PALLET_STACKS,
  hedgerowGroundY,
} from '@/world/hedgerow';
import type { Watcher } from '@/systems/stealth';
import { getAsset } from '@/models/registry';

function footprintOrThrow(id: string): { width: number; height: number; length: number } {
  const asset = getAsset(id);
  if (!asset) throw new Error(`hedgerow stealth: asset "${id}" is not registered`);
  return asset.footprint;
}

function volumeAt(
  at: { x: number; z: number },
  footprint: { width: number; height: number; length: number },
  concealment: number,
): CoverVolume {
  const y = hedgerowGroundY(at.x, at.z);
  return {
    min: { x: at.x - footprint.width / 2, y, z: at.z - footprint.length / 2 },
    max: { x: at.x + footprint.width / 2, y: y + footprint.height, z: at.z + footprint.length / 2 },
    concealment,
  };
}

/** Trailer, stacks, shed, both hedge runs and the far ditch bank; no decorative blockers. */
export function hedgerowOccluders(): Aabb[] {
  const trailer = footprintOrThrow('box-trailer');
  const pallet = footprintOrThrow('pallet-stack');
  const hedge = footprintOrThrow('hedge-section');
  return [
    { min: { x: 3 - trailer.length / 2, y: 0, z: 8.6 - trailer.width / 2 }, max: { x: 3 + trailer.length / 2, y: trailer.height, z: 8.6 + trailer.width / 2 } },
    ...PALLET_STACKS.map((at) => volumeAt(at, pallet, 0)),
    { min: { x: -11, y: 0, z: 10.45 }, max: { x: 1, y: 3.4, z: 10.75 } },
    // The gap is an omitted section, not a transparent blocker. Splitting this
    // run keeps the light/sight field honest about the one way through it.
    { min: { x: -20 - hedge.width / 2, y: hedgerowGroundY(-20, -20.9), z: -20.9 - hedge.length / 2 }, max: { x: -7, y: hedgerowGroundY(-7, -20.9) + hedge.height, z: -20.9 + hedge.length / 2 } },
    { min: { x: -5, y: hedgerowGroundY(-5, -20.9), z: -20.9 - hedge.length / 2 }, max: { x: 12 + hedge.width / 2, y: hedgerowGroundY(12, -20.9) + hedge.height, z: -20.9 + hedge.length / 2 } },
    { min: { x: -30 - hedge.width / 2, y: hedgerowGroundY(-30, -86), z: -86 - hedge.length / 2 }, max: { x: 24 + hedge.width / 2, y: hedgerowGroundY(24, -86) + hedge.height, z: -86 + hedge.length / 2 } },
    { min: { x: DITCH_AT.minX, y: DITCH_AT.bedY, z: -20.4 }, max: { x: DITCH_AT.maxX, y: hedgerowGroundY(0, -20.4), z: -19.9 } },
  ];
}

/** Cover and rain shelter are deliberately the same authored volumes. */
export function hedgerowCover(): CoverVolume[] {
  const trailer = footprintOrThrow('box-trailer');
  const pallet = footprintOrThrow('pallet-stack');
  const bin = footprintOrThrow('wheeled-bin');
  return [
    { min: { x: 3 - trailer.length / 2, y: 0, z: 8.6 - trailer.width / 2 }, max: { x: 3 + trailer.length / 2, y: 0.8, z: 8.6 + trailer.width / 2 }, concealment: 0.75 },
    ...PALLET_STACKS.map((at) => volumeAt(at, pallet, 0.7)),
    // ── The lee of the shed wall, and it reaches to z = 9.8 on purpose ────────
    //
    // This is **the men's ground**: `OutDogs` seeds a line of scent along it at
    // construction, at `handlerAt.z - 0.6` = 10.0, because the handler and the
    // stockmen have been walking it all evening. Stage 1's entire staging is the
    // player watching two dogs work that line and be right about it — rule 1
    // taught on somebody else's trail before it is ever used on hers.
    //
    // **It was z 10.1 … 10.9, and 10.0 is outside that**, so every seeded sample
    // was scored as exposed, died in `exposedLifetimeSeconds`, and the dogs had
    // nothing whatever to work in stage 1. A tenth of a metre, and it deletes a
    // stage. Reaching to 9.8 gives the wall a metre of lee, which is also what a
    // 3.4 m wall physically shelters.
    //
    // The general shape of the bug is worth the paragraph: a shelter volume and
    // the thing that deposits into it are authored in two files, and nothing
    // checks that the second lands inside the first. `out-beat.spec.ts`'s scent
    // delta is what checks it now — `OutDogs.debug.shelteredSamples` is non-zero
    // before she has deposited anything at all.
    { min: { x: -11, y: 0, z: 9.8 }, max: { x: 1, y: 1.1, z: 10.9 }, concealment: 0.7 },
    ...BINS.map((at) => volumeAt(at, bin, 0.7)),
    // The channel, and it is the strongest cover in the room at 0.8. She stands
    // on the bed 0.42 m below the far bank's sight line, which is mechanically why
    // the dogs stop and cast about instead of crossing (D-115).
    //
    // `channelFarZ` is the SMALLER z and so it is the `min`. This line had the two
    // the other way round and the volume was therefore empty — see the note on
    // `DITCH_AT` in `hedgerow.ts` for what that cost and why the fields are now
    // named by bank rather than by min/max.
    { min: { x: DITCH_AT.minX, y: DITCH_AT.bedY, z: DITCH_AT.channelFarZ }, max: { x: DITCH_AT.maxX, y: DITCH_AT.waterY + 0.5, z: DITCH_AT.channelNearZ }, concealment: 0.8 },
    { min: { x: HEDGE_GAP_X - 1, y: hedgerowGroundY(HEDGE_GAP_X, -20.9), z: -21.4 }, max: { x: HEDGE_GAP_X + 1, y: hedgerowGroundY(HEDGE_GAP_X, -20.9) + 1.2, z: -20.4 }, concealment: 0.65 },
  ];
}

/**
 * Bake the mandatory grid without inventing fixtures or pools of light.
 *
 * This exterior is lit entirely by dawn and the open sky, so its stealth
 * exposure is uniformly fully legible. `bakeLightGrid` supplies the standard
 * bounds/cell contract, then every sample is set to the same sky term: no
 * emitter, blocker, or decorative variation can make one patch falsely dark.
 */
/**
 * The one statement of the region both grids describe.
 *
 * It was written out twice — once per grid — for exactly one build. Two copies
 * of a bounds literal is how a room comes to have a day grid and a night grid
 * that disagree about where the room is, and `sample()` returns 0 outside its
 * declared bounds, so the symptom would be a watcher that goes blind in a strip
 * of field rather than anything that looks like a typo.
 */
const HEDGEROW_LIGHT_GRID_BOUNDS = { minX: -28, minZ: -86, width: 50, depth: 97 } as const;

export function bakeHedgerowLightGrid(): LightGrid {
  const grid = bakeLightGrid(HEDGEROW_LIGHT_GRID_BOUNDS, []);
  grid.values.fill(1);
  return {
    ...grid,
    sample: (): number => 1,
  };
}

/**
 * The day grid is 1.0 — "uniformly fully legible". The night grid is 0.90, and
 * the small size of that step is the point.
 *
 * What a stealth grid has to agree with is what the player can see, because
 * spec §5.1 says an unexplainable detection is a bug. So the number is derived
 * from where the ground lands on the toon ramp, which is irradiance over
 * `exposureReference`:
 *
 *     day   key 0.92 × ŷ 0.28 + hemi 0.46 + ambient 0.14 = 0.858 / 1.52 = 0.564
 *     night key 1.35 × ŷ 0.361 + hemi 0.10 + ambient 0.04 = 0.627 / 1.20 = 0.523
 *
 * **0.523 / 0.564 = 0.93.** This game's night is not dark, it is cold — the
 * `COLOUR_ARC` row says *"Moonlit. Saturated but cold"* and stage 1 found that
 * pushing the scene below the ramp's top band flattens it rather than darkening
 * it. 0.90 gives the moon a little credit and stops there.
 *
 * **The consequence is a design statement, not a shortfall: the night does not
 * hide her. The ditch does.** `hedgerowCover()` puts 0.8 concealment in the
 * channel, 0.75 under the trailer, 0.7 on the stacks, on the bins and in the lee
 * of the shed wall, and 0.65 in the hedge gap; those are the only places in this
 * room where she is hard to see. Anyone who wants the field itself to conceal
 * must change this number on purpose and say why.
 *
 * **None of them is inside the night dog's cone.** It stands at z −17.2 facing
 * +Z with 0.80 rad and 15 m: the channel is directly behind it at 3.14 rad, and
 * the stacks, bins and trailer are all past 23 m. So for the stage-2 watcher
 * this grid and this cover list are correct data that nothing yet consumes — its
 * blindness is entirely cone and range. **Stage 3 is the first time cover can
 * matter**, because it is what lets the dog leave its post and look down the
 * channel, and it wants its own gate on the day it does.
 */
export const HEDGEROW_NIGHT_EXPOSURE = 0.90;

export function bakeHedgerowNightLightGrid(): LightGrid {
  const grid = bakeLightGrid(HEDGEROW_LIGHT_GRID_BOUNDS, []);
  grid.values.fill(HEDGEROW_NIGHT_EXPOSURE);
  return { ...grid, sample: (): number => HEDGEROW_NIGHT_EXPOSURE };
}

/**
 * The dog on the near bank, and every number in it is a choice.
 *
 * **Where.** `NIGHT_DOG_AT` puts it at the foot of the fall facing +Z back up
 * it, which is the plan's stage B — *"the ground between the huddle and the
 * route down"*. The top of the field is outside its 15 m range, so the player
 * learns the verb in safety and meets the cost downfield; that is a consequence
 * of two coordinates and not of a rule anywhere.
 *
 * **How wide and how far.** 0.80 rad and 15 m sit inside the family the other
 * three watchers already use — `yardDirector.ts` runs Kestner at 0.384/9 calm
 * and 0.960/18 alert, `corridorScene.ts`'s patrols at 0.45 and 0.42. A dog
 * quartering open ground is wider than a man at a desk and shorter-sighted than
 * a man with a torch.
 *
 * **How high.** 0.55 m is a dog's head: `DOG_SCALE.withers` is 0.48 and its
 * skull sits above that. It matters because it is one end of every ray this
 * room casts, and the ditch's far bank is 0.42 m above the bed she wades in —
 * a watcher authored at chest height would see straight down into the channel
 * that D-115 built to hide her.
 *
 * It does not move. Stage 3 gives it legs, and gives a detection a consequence
 * in the same session, because either one without the other is a cost the
 * player cannot answer.
 */
export function hedgerowNightWatcher(): Watcher {
  return {
    id: 'night-dog',
    position: {
      x: NIGHT_DOG_AT.x,
      y: hedgerowGroundY(NIGHT_DOG_AT.x, NIGHT_DOG_AT.z),
      z: NIGHT_DOG_AT.z,
    },
    facing: NIGHT_DOG_AT.facing,
    coneHalfAngle: 0.80,
    range: 15,
    eyeHeight: 0.55,
  };
}
